import { create } from 'zustand';
import { api } from '../services/apiService';
import { AGENTS } from '../utils/agents';
import type { AgentWithCount, AgentDetail } from '../types/agents';

interface AgentFilter {
  csuite: string | null;
  department: string | null;
  search: string;
}

interface AgentState {
  agents: AgentWithCount[];
  selectedAgent: AgentDetail | null;
  filter: AgentFilter;
  loading: boolean;
  fetchAgents: () => Promise<void>;
  fetchAgentDetail: (slug: string) => Promise<void>;
  setFilter: (filter: Partial<AgentFilter>) => void;
  setSelectedAgent: (agent: AgentDetail | null) => void;
}

// C-Suite slug to department mapping for filtering
const CSUITE_DEPARTMENTS: Record<string, string[]> = {
  'cfo-agent': ['11-Analytics', '12-Finance'],
  'cmo-agent': ['01-Brand', '03-Campaigns', '05-Email', '06-Socials', '07-Ads', '08-Website', '09-UGC'],
  'coo-agent': ['00-Admin', '02-Products', '10-Marketplaces', '13-Codebases'],
  'cco-agent': ['04-Content'],
};

export const useAgentStore = create<AgentState>((set, get) => ({
  agents: AGENTS.map(a => ({ ...a, activityCount: 0 })),
  selectedAgent: null,
  filter: { csuite: null, department: null, search: '' },
  loading: false,

  fetchAgents: async () => {
    try {
      const agents = await api.get<AgentWithCount[]>('/agents');
      set({ agents });
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    }
  },

  fetchAgentDetail: async (slug: string) => {
    try {
      const detail = await api.get<AgentDetail>(`/agents/${slug}`);
      set({ selectedAgent: detail });
    } catch (err) {
      console.error('Failed to fetch agent detail:', err);
    }
  },

  setFilter: (partial: Partial<AgentFilter>) => {
    set(state => ({
      filter: { ...state.filter, ...partial },
    }));
  },

  setSelectedAgent: (agent: AgentDetail | null) => set({ selectedAgent: agent }),
}));

// Selector to get filtered agents
export function useFilteredAgents(): AgentWithCount[] {
  const agents = useAgentStore(s => s.agents);
  const filter = useAgentStore(s => s.filter);

  return agents.filter(agent => {
    // C-Suite filter: show the C-Suite agent + their direct reports' departments
    if (filter.csuite) {
      const depts = CSUITE_DEPARTMENTS[filter.csuite] || [];
      const isTheCsuite = agent.slug === filter.csuite;
      const isInDept = depts.includes(agent.department);
      if (!isTheCsuite && !isInDept) return false;
    }

    // Department filter
    if (filter.department) {
      if (agent.department !== filter.department) return false;
    }

    // Search filter
    if (filter.search) {
      const q = filter.search.toLowerCase();
      const match =
        agent.name.toLowerCase().includes(q) ||
        agent.title.toLowerCase().includes(q) ||
        agent.shortName.toLowerCase().includes(q);
      if (!match) return false;
    }

    return true;
  });
}
